import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';
import { EventCalendarDataService } from 'src/app/modules/event-calendar/event-calendar.data.service';
import { CoreService } from 'src/app/services/core/core.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { DialogService } from 'src/app/services/core/dialog.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';

import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';

import {
  ScheduleModule,
  DayService,
  WeekService,
  MonthService,
  AgendaService,
  TimelineMonthService,
  YearService,
  ResizeService,
  DragAndDropService,
  addDays,
  addMonths,
  addYears,
  resetTime,
  WEEK_LENGTH,
  ActionEventArgs,
  MS_PER_MINUTE,
  EventRenderedArgs,
  MS_PER_DAY,
  getWeekFirstDate,
  getWeekLastDate,
  EventSettingsModel,
  View,
  ScheduleComponent,
  GroupModel,
  PopupOpenEventArgs,
  EJ2Instance,
} from '@syncfusion/ej2-angular-schedule';

import { ViewEncapsulation } from '@angular/core';
import { EventDialogComponent } from 'src/app/modules/event-calendar/calendar-list/dialog/event-dialog/event-dialog.component';
import { ConfirmationPopupComponent } from 'src/app/modules/shared/confirmation-popup/confirmation-popup.component';
import { SideMenuService } from 'src/app/modules/shared/sidemenu.service';
import { Subscription } from 'rxjs';
import { HelperService } from 'src/app/services/helper.service';
import { DatePipe } from '@angular/common';
import { CommunicationServiceService } from '../../widgets/services/communication-service.service';
import { ViewTaskDetailComponent } from '../../sale/task/view-task-detail/view-task-detail.component';
import { ViewTicketDetailComponent } from '../../ticket/view-ticket-detail/view-ticket-detail.component';
import { CreateEditBidComponent } from '../../sale/bid/create-edit-bid/create-edit-bid.component';
import { BidDataService } from '../../sale/bid/bid.data.service';


@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    providers: [
        DayService,
        WeekService,
        MonthService,
        AgendaService,
        TimelineMonthService,
        YearService,
        DragAndDropService,
        ResizeService,
        DatePipe,
    ],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class CalendarComponent implements OnInit, OnDestroy {


  @Input() detailID: any = '';
  @Input() detailCalendarID: any = '';
  @Input() moduleID: any = '';
  @Input() createEventPermission: any = '';
  @Input() editEventPermission: any = '';
  @Input() deleteEventPermission: any = '';
  @Input() viewEventPermission: any = '';
  @Input() moveEventPermission: any = '';
  @Input() eventBackgroundColorSettingKey: any = '';
  @Input() isLocked: boolean = false;
  /**
   * Opt-in toolbar trims used by read-only / minimal-chrome hosts
   * (e.g. the warehouse delivery calendar modal). Default false so
   * every existing consumer keeps the full toolbar without changes.
   */
  @Input() hideCreateButton: boolean = false;
  @Input() hideSettingsButton: boolean = false;
  /**
   * When true, clicking an empty cell is a no-op (no event-creation
   * dialog opens). Existing event-click behavior is unchanged — users
   * can still click delivery events to view/edit them. Used by the
   * warehouse delivery calendar, which doesn't create events from
   * inside the calendar grid. Default false preserves the existing
   * behavior for every other consumer.
   */
  @Input() disableCellCreate: boolean = false;
  /**
   * Opt-in by the warehouse-detail's calendar instance only.
   * When true, dragging a synthetic delivery event onto a new
   * date/time does NOT call the generic events `move` endpoint
   * (which would 500 for `delivery-N` ids). Instead
   * `deliveryDragDropRequested` fires with the parsed source +
   * target so the host can show a time-confirm popup and call the
   * dedicated reschedule endpoint. Every other consumer leaves
   * this flag at the default (false) and keeps the existing move
   * flow untouched.
   */
  @Input() routeDeliveryDragDropExternally: boolean = false;
  /**
   * Opt-in by the warehouse-detail's calendar instance only. When
   * true, clicking Edit on a delivery event does NOT open the generic
   * event-edit dialog. Instead `deliveryEventEditRequested` fires
   * with the parsed delivery id so the host can open the Schedule
   * Wizard (in a dialog over the calendar). Every other consumer
   * keeps the standard event editor.
   */
  @Input() routeDeliveryEditsExternally: boolean = false;
  /**
   * Opt-in by the warehouse-detail's calendar instance only. When
   * true, the QuickInfo popup for a delivery event shows a coloured
   * status pill. Default false — no other calendar renders it.
   */
  @Input() showDeliveryStatusInQuickInfo: boolean = false;
  /**
   * Opt-in by the warehouse-detail's calendar instance only. When
   * true, delivery events that share an hour bucket on the same day
   * are collapsed to a single "primary" chip (the earliest) plus a
   * `+N` badge. The siblings are accessible via the badge's popup.
   * Default false — every other calendar renders each event
   * individually as before.
   */
  @Input() groupConcurrentDeliveries: boolean = false;
  @Output() onEventUpdated: EventEmitter<any> = new EventEmitter<any>();
  /**
   * Fired when `routeDeliveryEditsExternally` is on and the operator
   * edits a delivery event. Carries the numeric delivery id so the
   * host can open its own edit wizard.
   */
  @Output() deliveryEventEditRequested: EventEmitter<number> = new EventEmitter<number>();
  /**
   * Fired when the operator clicks the `+N` overflow badge on a
   * grouped delivery event. Carries the primary event plus the
   * hidden siblings in that bucket so the host can render a popup
   * listing all of them. Only emits when `groupConcurrentDeliveries`
   * is on.
   */
  @Output() deliveryBucketOverflowClicked: EventEmitter<{
    primary: any;
    siblings: any[];
  }> = new EventEmitter();
  /**
   * Fired when `disableCellCreate` is on and the operator clicks an
   * empty date cell. Carries the clicked date ('YYYY-MM-DD') so the
   * host can open its own create flow (e.g. the Schedule Delivery
   * wizard) with the date pre-filled, instead of the generic
   * event-creation dialog.
   */
  @Output() cellDateClicked: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Fired when `routeDeliveryDragDropExternally` is on and the
   * operator drags a delivery event to a new date/time. The host
   * is expected to either:
   *   - call PATCH /deliveries/{id}/reschedule on confirm and then
   *     `refreshEventAppointments(true)` to repaint, or
   *   - call `refreshEventAppointments(true)` to snap the row back
   *     to its original position on cancel / failure.
   */
  @Output() deliveryDragDropRequested: EventEmitter<{
    deliveryId: number;
    newDate: string;       // 'YYYY-MM-DD'
    newTime: string;       // 'HH:MM' (24h)
    originalDate: string;
    originalTime: string;
    vendorName?: string | null;
    deliveryCode?: string | null;
  }> = new EventEmitter();
  /**
   * Fired whenever the events fetch completes (success or error).
   * Consumers (e.g. modal hosts) can use this to hide a loading
   * placeholder once the calendar has data. Purely additive — any
   * caller that doesn't bind this output ignores it.
   */
  @Output() eventsLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  // View mode toggle (calendar or list)
  viewMode: 'calendar' | 'list' = 'calendar';

  // Search functionality
  search: string = '';
  private searchInput = new Subject<string>();
  private readonly debounceTimeMs = 500;
  private searchSubscription!: Subscription;

  // List view data
  eventListData: any[] = [];

  // Loading and error states
  isLoadingEvents: boolean = false;
  hasError: boolean = false;

  referenceId: any = '';
  bidsEvents: any = [];
  userCalendarSettings: any = {};
  eventSettings: EventSettingsModel = {
    dataSource: [],
    template: '#eventTemplate',
  };
  currentView: View = 'Month';
  views: View[] = ['Day', 'Week', 'Month', 'Agenda', 'Year'];

  // UI setting: background color basis for Bid calendar events
  calendarBgSource: 'default' | 'status' = 'default';

  // Add this property to store initial drag data
  private initialDragData: any = null;

  @ViewChild('schedule', { static: false }) schedule!: ScheduleComponent;
  viewStartDate?: Date;
  viewEndDate?: Date;
  lastRange?: { start: string; end: string };
  navDebounce?: any;
  public refreshDebounce?: any;
  public scrumDataSubscription!: Subscription;
  public leftSidebarMenuToggleSubscription!: Subscription;
  constructor(
    public eventCalendarDataService: EventCalendarDataService,
    public coreService: CoreService,
    public toasterService: ToasterService,
    private dialogService: DialogService,
    public dialog: NgDialogAnimationService,
    private sideMenuService: SideMenuService,
    public helperService: HelperService,
    public scrumCommunication: CommunicationServiceService,
    private datePipe: DatePipe,
    private bidDataService: BidDataService,
    private router: Router,
  ) {
    this.referenceId = this.detailID;
  }

  ngOnInit(): void {
    // no-op
    this.getLeftSidebarMenuToggleSubscription();
    this.initSubscription();
    this.setupSearchSubscription();
    console.log('this.createEventPermission', this.createEventPermission);
    console.log('this.editEventPermission', this.editEventPermission);
    console.log('this.deleteEventPermission', this.deleteEventPermission);
    console.log('this.viewEventPermission', this.viewEventPermission);
    console.log('this.moveEventPermission', this.moveEventPermission);
    console.log('this.moduleID', this.moduleID);
    console.log('this.detailID', this.detailID);
    console.log('this.detailCalendarID', this.detailCalendarID);
  }

  ngAfterViewInit(): void {
    // Single initial load
    setTimeout(() => this.refreshEventAppointments(true), 0);
  }

  isEventCreate(): boolean {
    // Always check permission, regardless of lock status
    // Lock status will be handled by disabled attribute in template
    return this.helperService.isPermissionAvailable(
      this.createEventPermission
    );
  }
  isEventEdit(): boolean {
    if (this.isLocked) {
      return false;
    }
    return this.helperService.isPermissionAvailable(
      this.editEventPermission
    );
  }
  isEventDelete(): boolean {
    if (this.isLocked) {
      return false;
    }
    return this.helperService.isPermissionAvailable(
      this.deleteEventPermission
    );
  }
  isEventMove(): boolean {
    if (this.isLocked) {
      return false;
    }
    return this.helperService.isPermissionAvailable(
      this.moveEventPermission
    );
  }

  private captureCurrentViewRange(): boolean {
    if (
      !this.schedule ||
      typeof (this.schedule as any).getCurrentViewDates !== 'function'
    )
      return false;
    const dates: Date[] = (this.schedule as any).getCurrentViewDates();
    if (!dates?.length) return false;
    this.viewStartDate = dates[0];
    this.viewEndDate = dates[dates.length - 1];
    return true;
  }

  getModuleWithEvents(forceReload: boolean = false) {
    // For list view, we might not have viewStartDate/viewEndDate
    // So we need to handle that case
    if (this.viewMode === 'calendar') {
      if (!this.viewStartDate || !this.viewEndDate) return;
    } else {
      // For list view, use a wider date range if not available
      if (!this.viewStartDate || !this.viewEndDate) {
        const now = new Date();
        this.viewStartDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        this.viewEndDate = new Date(now.getFullYear(), now.getMonth() + 6, 0);
      }
    }

    const start = this.formatDateForAPI(this.viewStartDate);
    const end = this.formatDateForAPI(this.viewEndDate);

    // Always reload if search term exists or if forced
    if (
      !forceReload &&
      !this.search &&
      this.lastRange &&
      this.lastRange.start === start &&
      this.lastRange.end === end
    ) {
      return; // no change, skip
    }
    this.lastRange = { start, end };

    // Set loading state
    this.isLoadingEvents = true;
    this.hasError = false;

    this.eventCalendarDataService
      .getModuleWithEvents(this.detailID, this.moduleID, start, end, this.search)
      .subscribe({
        next: (response: any) => {
          this.isLoadingEvents = false;
          this.hasError = false;
          this.bidsEvents = response?.data || [];
          this.eventListData = this.bidsEvents; // Store for list view
          this.userCalendarSettings = response?.settings || {};
          console.log('userCalendarSettings===', this.userCalendarSettings);
          // Sync UI radio with loaded user setting
          this.calendarBgSource =
            this.userCalendarSettings[this.eventBackgroundColorSettingKey] != undefined
              && this.userCalendarSettings[this.eventBackgroundColorSettingKey] === 2
              ? 'status'
              : 'default';
          const scheduleEvents = this.bucketConcurrentDeliveries(
            this.mapApiEventsToScheduleEvents(this.bidsEvents)
          );
          console.log('scheduleEvents', scheduleEvents);
          this.eventSettings = {
            ...this.eventSettings,
            dataSource: scheduleEvents,
          };
          // Notify hosts (e.g. delivery calendar modal) that the
          // events fetch is complete so they can dismiss a loading
          // placeholder.
          this.eventsLoaded.emit(true);
        },
        error: (error: any) => {
          this.isLoadingEvents = false;
          this.hasError = true;
          console.error('Error fetching events:', error);
          // Set empty arrays on error
          this.bidsEvents = [];
          this.eventListData = [];
          this.eventsLoaded.emit(false);
        }
      });
  }
  getLeftSidebarMenuToggleSubscription() {
    this.leftSidebarMenuToggleSubscription = this.sideMenuService
      .getAppSideMenuToggle()
      .subscribe((response) => {
        this.eventSettings.dataSource = [];
        const scheduleEvents = this.bucketConcurrentDeliveries(
          this.mapApiEventsToScheduleEvents(this.bidsEvents)
        );
        this.eventSettings = {
          ...this.eventSettings,
          dataSource: scheduleEvents,
        };
      });
  }
  /**
   * Bucket concurrent delivery events so the Day / Week / TwoWeeks
   * grids show only the earliest in each hour-of-day slot, with a
   * `+N` badge linking to the rest via the overflow popup.
   *
   * Active only when `groupConcurrentDeliveries` is on (warehouse
   * delivery calendar). Real events, tasks, tickets pass through
   * untouched.
   *
   * Bucket key = `YYYY-MM-DD HH` of the event's local start time.
   * Earliest event in the bucket becomes the primary and gets a
   * `_bucketSiblings` array attached; siblings get `_isBucketHidden`
   * set so `onEventRendered` cancels their render.
   */
  private bucketConcurrentDeliveries(events: any[]): any[] {
    if (!this.groupConcurrentDeliveries) return events;

    const buckets = new Map<string, any[]>();
    for (const e of events) {
      if (!e?.is_delivery) continue;
      const start = e.StartTime instanceof Date
        ? e.StartTime
        : new Date(e.StartTime);
      if (isNaN(start.getTime())) continue;
      // Local YYYY-MM-DD HH key so events crossing midnight land in
      // distinct buckets and the operator's local view matches.
      const y = start.getFullYear();
      const m = (start.getMonth() + 1).toString().padStart(2, '0');
      const d = start.getDate().toString().padStart(2, '0');
      const h = start.getHours().toString().padStart(2, '0');
      const key = `${y}-${m}-${d} ${h}`;
      const list = buckets.get(key) ?? [];
      list.push(e);
      buckets.set(key, list);
    }

    for (const list of buckets.values()) {
      if (list.length < 2) continue;
      // Earliest start wins as primary; ties keep the array's first.
      list.sort((a, b) => {
        const ta = (a.StartTime instanceof Date ? a.StartTime : new Date(a.StartTime)).getTime();
        const tb = (b.StartTime instanceof Date ? b.StartTime : new Date(b.StartTime)).getTime();
        return ta - tb;
      });
      const [primary, ...siblings] = list;
      primary._bucketSiblings = siblings;
      for (const s of siblings) {
        s._isBucketHidden = true;
      }
    }

    return events;
  }

  /**
   * Called by the event template's `+N` badge click. Emits the
   * primary + its siblings so the host can open the overflow popup.
   * Stops propagation so the click doesn't also fire the standard
   * event-edit / QuickInfo flow.
   */
  onBucketOverflowClick(event: Event, data: any): void {
    event.stopPropagation();
    event.preventDefault();
    if (! data?._bucketSiblings?.length) return;
    this.deliveryBucketOverflowClicked.emit({
      primary: data,
      siblings: data._bucketSiblings,
    });
  }

  /**
   * If `end` falls on a later calendar day than `start`, collapse it back
   * to `start` so the appointment only occupies its start date instead of
   * spanning every day through the real end date. Same-day events (the
   * normal case — a timed meeting, an all-day single date) are returned
   * unchanged.
   */
  private clampEndToStartDay(start: Date, end: Date): Date {
    return start.toDateString() === end.toDateString() ? end : new Date(start);
  }

  /**
   * Dragging a start-date-only (clamped) multi-day event must still move
   * its real end date by the same delta, not collapse it to the new
   * start — otherwise rescheduling a Job's calendar bar would silently
   * shrink its deadline to match the drop date. Uses the event's
   * un-clamped `__trueEndTime` (captured at map time) plus the original
   * duration, so the whole span shifts together.
   */
  private computeDraggedEndTime(eventData: any, newStartTime: Date): Date {
    const trueEnd: Date | undefined = eventData.__trueEndTime;
    const originalStart = this.initialDragData?.startTime;
    if (!trueEnd || !originalStart || isNaN(trueEnd.getTime())) {
      return new Date(eventData.EndTime);
    }
    const durationMs = trueEnd.getTime() - originalStart.getTime();
    return new Date(newStartTime.getTime() + durationMs);
  }

  private mapApiEventsToScheduleEvents(apiEvents: any[]): any[] {
    const events: any[] = [];

    (apiEvents || []).forEach((event: any) => {
      // Decide colors based on user setting
      const bidBgMode =
        this.userCalendarSettings?.[this.eventBackgroundColorSettingKey];
      let backgroundColor: string;
      let textColor: string;

      if (bidBgMode === 2 && event?.status?.color) {
        // Background by status color
        backgroundColor = event.status.color;

        // Text by main category color (if available), otherwise readable on status bg
        const categories = event.calendar_categories || [];
        const mainCategory =
          categories.find((cat: any) => cat.is_main === 1) || categories[0];
        if (mainCategory?.color) {
          textColor = mainCategory.color;
        } else {
          // Fallback: readable text derived from status background
          textColor = this.isDarkColor(backgroundColor)
            ? this.lightenColor(backgroundColor, 80)
            : this.darkenColor(backgroundColor, 35);
        }
      } else {
        // Existing logic (default/background by main category or API, text for readability)
        const fromApiBg = event.backgroundColor || event.color;
        const fromApiText = event.textColor;
        const fromCategories = this.calculateEventColors(
          event.calendar_categories || []
        );
        backgroundColor =
          fromApiBg || fromCategories.backgroundColor || '#FBFAFF';
        textColor = fromApiText || fromCategories.textColor || '#29304E';
      }

      // Create the main event
      const startTime = new Date(event.start_date_time);
      const trueEndTime = new Date(event.end_date_time);
      const mainEvent = {
        ...event,
        Id: event.id,
        Subject: event.title,
        StartTime: startTime,
        // Render on the start date only — a multi-day span (e.g. a Job's
        // start_date -> deadline) would otherwise draw as a long bar
        // across every day in between. __trueEndTime keeps the real end
        // around so drag-to-reschedule still moves the whole span intact
        // (see computeDraggedEndTime).
        EndTime: this.clampEndToStartDay(startTime, trueEndTime),
        __trueEndTime: trueEndTime,
        CalendarId: event.calendar_id,
        Description: event.description,
        IsAllDay: event.is_all_day === 1,
        backgroundColor,
        textColor,
        calendar_categories: event.calendar_categories || [],
        isAdditionalTimeSlotEvent: false,
      };

      events.push(mainEvent);

      if (
        event.additional_time_slots &&
        event.additional_time_slots.length > 0
      ) {
        const additionalEvents = this.createAdditionalTimeSlotEvents(
          event,
          backgroundColor,
          textColor
        );
        events.push(...additionalEvents);
      }
    });

    return events;
  }

  /**
   * Creates additional events for each time slot in additional_time_slots
   * @param event - The original event object
   * @param backgroundColor - Background color for the events
   * @param textColor - Text color for the events
   * @returns Array of additional events
   */
  private createAdditionalTimeSlotEvents(
    event: any,
    backgroundColor: string,
    textColor: string
  ): any[] {
    const additionalEvents: any[] = [];

    event.additional_time_slots.forEach((timeSlot: any, index: number) => {
      // Create start and end datetime strings by combining date and time
      const startDateTime = `${timeSlot.start_date} ${timeSlot.start_time}`;
      const endDateTime = `${timeSlot.end_date} ${timeSlot.end_time}`;

      // Create the additional event
      const slotStart = new Date(startDateTime);
      const slotTrueEnd = new Date(endDateTime);
      const additionalEvent = {
        ...event,
        Id: `${event.id}_slot_${timeSlot.id}`,
        Subject: event.title,
        StartTime: slotStart,
        EndTime: this.clampEndToStartDay(slotStart, slotTrueEnd),
        __trueEndTime: slotTrueEnd,
        CalendarId: event.calendar_id,
        Description: event.description,
        IsAllDay: false,
        backgroundColor: backgroundColor,
        textColor: textColor,
        calendar_categories: event.calendar_categories || [],
        isAdditionalTimeSlotEvent: true,
        slotId: timeSlot.id,
        originalEventId: event.id,
        timeSlotIndex: index,
      };

      additionalEvents.push(additionalEvent);
    });

    return additionalEvents;
  }

  // Persist user choice for how to color Bid calendar events
  onBidCalendarBgChange(value: 'default' | 'status'): void {
    if (this.isLocked) {
      return;
    }
    this.calendarBgSource = value;
    const payload: any = {
      [this.eventBackgroundColorSettingKey]: value === 'status' ? 2 : 1,
    };
    console.log('payload===', payload);
    this.eventCalendarDataService.saveUserCalendarSettings(payload).subscribe({
      next: (response: any) => {
        console.log('response', response);
        this.toasterService.showSuccessMessage(response?.message);
        // Optional: refresh events if backend uses this setting to compute colors
        this.refreshEventAppointments(true);
      },
    });
  }

  // Also recalc on navigation between dates or views (debounced)
  onScheduleActionComplete(args: ActionEventArgs): void {
    if (
      args?.requestType === 'dateNavigate' ||
      args?.requestType === 'viewNavigate'
    ) {
      clearTimeout(this.navDebounce);
      this.navDebounce = setTimeout(
        () => this.refreshEventAppointments(true),
        120
      );
    }
  }

  refreshEventAppointments(forceReload: boolean = false): void {
    clearTimeout(this.refreshDebounce);
    this.refreshDebounce = setTimeout(() => {
      // For calendar view, capture date range from schedule
      // For list view, use existing range or default range
      if (this.viewMode === 'calendar') {
        if (this.captureCurrentViewRange()) {
          this.getModuleWithEvents(forceReload);
        }
      } else {
        // List view: ensure we have date range, then fetch
        if (!this.viewStartDate || !this.viewEndDate) {
          const now = new Date();
          this.viewStartDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
          this.viewEndDate = new Date(now.getFullYear(), now.getMonth() + 6, 0);
        }
        this.getModuleWithEvents(forceReload);
      }
    }, 150);
  }

  public onEventRendered(args: any): void {
    if (!args?.element || !args?.data) return;

    // Bucket guard — only ever true on a delivery sibling whose
    // parent has already taken the slot. Triple-gated: feature flag
    // (warehouse only) + the hidden marker (only set on delivery
    // siblings) + the `is_delivery` shape. Bid events, tasks,
    // tickets all pass through.
    if (
      this.groupConcurrentDeliveries &&
      args.data?.is_delivery &&
      args.data?._isBucketHidden
    ) {
      args.element.style.display = 'none';
      return;
    }

    const bg = args.data.backgroundColor || '#FBFAFF';
    const fg = args.data.textColor || '#29304E';

    args.element.style.backgroundColor = bg;

    const textEls = args.element.querySelectorAll(
      '.event-time, .event-title, .event-time-week, .event-title-week, .event-time-month, .event-title-month, .event-time-agenda, .event-title-agenda, .event-time-year, .event-title-year'
    );
    textEls.forEach((el: HTMLElement) => (el.style.color = fg));

    // Apply left status-indicator color
    const statusEls = args.element.querySelectorAll(
      '.event-status-indicator, .event-status-indicator-week, .event-status-indicator-month, .event-status-indicator-agenda, .event-status-indicator-year'
    );
    statusEls.forEach((el: HTMLElement) => {
      const isStatusMode =
        this.userCalendarSettings?.[this.eventBackgroundColorSettingKey] === 2 || this.calendarBgSource === 'status';
      if (isStatusMode) {
        // In status mode, use main category color for the indicator (both bg and border)
        const categories = (args?.data?.calendar_categories as any[]) || [];
        const mainCategory =
          categories.find((c: any) => c?.is_main === 1) || categories[0];
        // If no category color, fall back to text color (fg)
        const indicatorColor: string = (mainCategory?.color as string) || fg;
        el.style.backgroundColor = indicatorColor;
        el.style.borderColor = indicatorColor;
      } else {
        // Default mode: indicator uses status color; else transparent with readable border
        const sc = args?.data?.status?.color;
        if (sc) {
          el.style.backgroundColor = sc;
          el.style.borderColor = sc;
        } else {
          el.style.backgroundColor = 'transparent';
          el.style.borderColor = fg;
        }
      }
    });

    // Agenda wrapper tint (if used)
    const customAgenda = args.element.querySelector('.custom-content-agenda');
    if (customAgenda) (customAgenda as HTMLElement).style.backgroundColor = bg;
  }

  onCellClick(args: any): void {
    if (!this.isEventCreate()) {
      args.cancel = true;
      return;
    }
    // Check if the click was on the "+1 more" indicator
    const target = args.event?.target as HTMLElement;
    const moreIndicator = target?.closest('.e-more-indicator');

    if (moreIndicator) {
      // Allow the default Syncfusion popup to open for "+1 more"
      args.cancel = false;
      return;
    }

    const hasAppointment = target?.closest('.e-day');
    if (hasAppointment) {
      args.cancel = false;
      return;
    }

    // Handle regular cell click for creating new event
    args.cancel = true;

    // Hosts that don't want their grid to spawn the generic
    // event-creation dialog (e.g. the warehouse delivery calendar)
    // opt out via `disableCellCreate`. Instead of a pure no-op, emit
    // the clicked date so the host can open its own create flow
    // (the Schedule Delivery wizard) with the date pre-filled.
    if (this.disableCellCreate) {
      const clicked = args?.startTime instanceof Date
        ? args.startTime
        : (args?.startTime ? new Date(args.startTime) : null);
      if (clicked) {
        this.cellDateClicked.emit(this.formatDateForAPI(clicked));
      }
      return;
    }

    // Pass the clicked cell data with startTime
    this.openEventDialog(null, {
      startTime: args.startTime,
      endTime: args.endTime,
      isAllDay: args.isAllDay,
    });
  }

  onCellDoubleClick(args: any): void {
    args.cancel = true;
  }

  onDragStart(args: any): void {
    // Warehouse delivery override — opt-in by the warehouse-detail calendar.
    // A delivery drag routes to the host's time-confirm dialog (governed by
    // warehouse access), so allow it even when the generic move permission
    // (CALENDAR-DELIVERY-RELATED-CHANGE-EVENT-DATE) isn't seeded. Without this
    // the drag was cancelled here and the event snapped back before onDragStop
    // could emit.
    const isExternalDelivery =
      this.routeDeliveryDragDropExternally && this.isDeliveryEvent(args?.data);
    if (!isExternalDelivery && !this.isEventMove()) {
      args.cancel = true;
      return;
    }
    // Store the initial drag data for comparison
    this.initialDragData = {
      startTime: new Date(args.data.StartTime),
      endTime: new Date(args.data.EndTime),
      eventId: args.data.Id,
      slotId: args.data.slotId,
      isAdditionalTimeSlotEvent: args.data.isAdditionalTimeSlotEvent,
    };
  }

  /**
   * True when this calendar event represents a synthetic warehouse
   * delivery row. The feed stamps `is_delivery: true` and
   * `event_reference_type: 'warehouse'`; older payloads may only
   * carry one marker, so we tolerate either + the "delivery-N" id
   * pattern.
   */
  private isDeliveryEvent(data: any): boolean {
    if (!data) return false;
    if (data.is_delivery === true) return true;
    if (data.event_reference_type === 'warehouse') return true;
    const id = String(data.Id ?? data.id ?? '');
    return /^delivery-\d+$/.test(id);
  }

  /** Parse the numeric delivery id from a synthetic event. */
  private extractDeliveryId(data: any): number | null {
    if (!data) return null;
    const explicit = Number(data.delivery_id);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const raw = String(data.Id ?? data.id ?? '');
    const m = raw.match(/^delivery-(\d+)$/);
    return m ? Number(m[1]) : null;
  }

  onDragStop(args: any): void {
    // Warehouse delivery override — mirror onDragStart: a delivery drag routes
    // externally to the host (which enforces its own permissions), so don't
    // block it on the never-seeded generic move permission.
    const isExternalDelivery =
      this.routeDeliveryDragDropExternally && this.isDeliveryEvent(args?.data);
    if (!isExternalDelivery && !this.isEventMove()) {
      this.initialDragData = null;
      return;
    }
    if (args && args.data && args.data.Id) {
      const eventData = args.data;
      // Check if the drag actually changed the position
      if (this.initialDragData) {
        const currentStartTime = new Date(eventData.StartTime);
        const currentEndTime = new Date(eventData.EndTime);

        // Compare start and end times exactly (no tolerance)
        const startTimeChanged =
          currentStartTime.getTime() !==
          this.initialDragData.startTime.getTime();
        const endTimeChanged =
          currentEndTime.getTime() !== this.initialDragData.endTime.getTime();

        // If no change in time, don't call the API
        if (!startTimeChanged && !endTimeChanged) {
          this.initialDragData = null; // Reset for next drag
          return;
        }
      }

      // Format dates for API
      const startDate = new Date(eventData.StartTime);
      const endDate = this.computeDraggedEndTime(eventData, startDate);

      // Warehouse delivery override — opt-in by the warehouse-detail
      // calendar only. Skip the generic events `move` endpoint (it
      // would 500 on `delivery-N` ids) and emit a structured
      // drag-drop event the host can pop a time-confirm dialog for.
      if (this.routeDeliveryDragDropExternally && this.isDeliveryEvent(eventData)) {
        const deliveryId = this.extractDeliveryId(eventData);
        if (deliveryId !== null) {
          const orig = this.initialDragData;
          this.deliveryDragDropRequested.emit({
            deliveryId,
            newDate:      this.formatDateForAPI(startDate),
            newTime:      startDate.toTimeString().slice(0, 5),
            originalDate: orig ? this.formatDateForAPI(orig.startTime) : '',
            originalTime: orig ? orig.startTime.toTimeString().slice(0, 5) : '',
            vendorName:   eventData.title ?? eventData.vendor_name ?? null,
            deliveryCode: eventData.delivery_code ?? null,
          });
          this.initialDragData = null;
          return;
        }
      }

      const movePayload: any = {
        start_date: this.formatDateForAPI(startDate),
        start_time: this.formatTimeForAPI(startDate),
        end_date: this.formatDateForAPI(endDate),
        end_time: this.formatTimeForAPI(endDate),
      };

      // Check if this is an additional time slot event
      if (eventData.isAdditionalTimeSlotEvent === true) {
        // For additional slots, use slotId and add the flag
        movePayload.is_additional_time_slot_event = 1;

        this.eventCalendarDataService
          .moveAppointmentInCalendar(eventData?.slotId, movePayload) // Use slotId instead of Id
          .subscribe({
            next: (response: any) => {
              this.toasterService.showSuccessMessage(response?.message);
              this.refreshEventAppointments(true);
            },
          });
      } else {
        // For main events, use current logic
        this.eventCalendarDataService
          .moveAppointmentInCalendar(eventData?.Id, movePayload)
          .subscribe({
            next: (response: any) => {
              this.toasterService.showSuccessMessage(response?.message);
              this.refreshEventAppointments(true);
              this.onEventUpdated.emit(response?.data);
            },
          });
      }

      this.initialDragData = null;
    }
  }

  onResizeStart(args: any): void {
    console.log('onResizeStart==', args?.data);
    if (!this.isEventMove()) {
      args.cancel = true;
      return;
    }

    this.initialDragData = {
      startTime: new Date(args.data.StartTime),
      endTime: new Date(args.data.EndTime),
      eventId: args.data.Id,
      slotId: args.data.slotId,
      isAdditionalTimeSlotEvent: args.data.isAdditionalTimeSlotEvent,
    };
  }

  onResizeStop(args: any): void {
    console.log('onResizeStop==', args?.data);
    if (!this.isEventMove()) {
      args.cancel = true;
      return;
    }
    if (args && args.data && args.data.Id) {
      const eventData = args.data;
      console.log('eventData====', eventData);

      // Check if the drag actually changed the position
      if (this.initialDragData) {
        const currentStartTime = new Date(eventData.StartTime);
        const currentEndTime = new Date(eventData.EndTime);

        // Compare start and end times exactly (no tolerance)
        const startTimeChanged =
          currentStartTime.getTime() !==
          this.initialDragData.startTime.getTime();
        const endTimeChanged =
          currentEndTime.getTime() !== this.initialDragData.endTime.getTime();

        // If no change in time, don't call the API
        if (!startTimeChanged && !endTimeChanged) {
          console.log('No Draged change detected, skipping API call');
          this.initialDragData = null; // Reset for next drag
          return;
        }
      }

      // Format dates for API
      const startDate = new Date(eventData.StartTime);
      const endDate = new Date(eventData.EndTime);

      const movePayload: any = {
        start_date: this.formatDateForAPI(startDate),
        start_time: this.formatTimeForAPI(startDate),
        end_date: this.formatDateForAPI(endDate),
        end_time: this.formatTimeForAPI(endDate),
      };

      // Check if this is an additional time slot event
      if (eventData.isAdditionalTimeSlotEvent === true) {
        // For additional slots, use slotId and add the flag
        movePayload.is_additional_time_slot_event = 1;

        this.eventCalendarDataService
          .moveAppointmentInCalendar(eventData?.slotId, movePayload) // Use slotId instead of Id
          .subscribe({
            next: (response: any) => {
              this.toasterService.showSuccessMessage(response?.message);
              this.refreshEventAppointments(true);
            },
          });
      } else {
        // For main events, use current logic
        this.eventCalendarDataService
          .moveAppointmentInCalendar(eventData?.Id, movePayload)
          .subscribe({
            next: (response: any) => {
              this.toasterService.showSuccessMessage(response?.message);
              this.refreshEventAppointments(true);
            },
          });
      }

      this.initialDragData = null;
    }
  }

  private formatDateForAPI(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatTimeForAPI(date: Date): string {
    return date.toTimeString().split(' ')[0]; // Returns HH:MM:SS
  }

  public toolbarClick(args: ClickEventArgs): void {
    if (!args.item) {
      return;
    }

    // Handle create button click
    if (args?.item?.cssClass === 'e-create') {
      if (!this.isEventCreate()) {
        return;
      }
      this.openEventDialog(null, null, null);
    }
  }

  onPopupOpen(args: PopupOpenEventArgs): void {
    // Make delete/edit use custom dialogs
    if (args.type === 'DeleteAlert') {
      args.cancel = true;
      if (!this.isEventDelete()) {
        return;
      }
      this.eventDeleteConfirmation(args.data);
      return;
    }
    if (args.type === 'Editor') {
      args.cancel = true;

      // Warehouse delivery override — opt-in by the warehouse-detail
      // calendar only. Emit the delivery id so the host opens the delivery
      // edit dialog instead of the generic event-edit dialog.
      //
      // Checked BEFORE the generic isEventEdit() gate on purpose: a delivery
      // edit routes to the warehouse delivery dialog (governed by warehouse
      // access), not the generic calendar-event editor. The delivery
      // calendar's edit permission (CALENDAR-DELIVERY-RELATED-EDIT-EVENT) is
      // not seeded, so gating here made isEventEdit() return false and
      // silently swallowed every delivery edit click. Other consumers leave
      // routeDeliveryEditsExternally off and keep the standard gate below.
      if (this.routeDeliveryEditsExternally && this.isDeliveryEvent(args.data)) {
        const id = this.extractDeliveryId(args.data);
        if (id !== null) {
          this.deliveryEventEditRequested.emit(id);
          return;
        }
      }

      if (!this.isEventEdit()) {
        return;
      }
      if (args.data?.event_reference_type === 'task') {
        this.openTaskDetail(args.data);
        return;
      }
      if (args.data?.event_reference_type === 'ticket') {
        this.openTicketDetail(args.data);
        return;
      }
      if (args.data?.event_reference_type === 'bid' && args.data?.reference_id) {
        this.openBidEditWizard(args.data.reference_id);
        return;
      }
      this.openEventDialog(null, null, args.data);
      return;
    }

    // Style the quick-info popup header to match the appointment color (all views incl. Agenda)
    if (args.type === 'QuickInfo' && args.element && args.data) {
      const data: any = args.data as any;
      const bg = data.backgroundColor || '#FBFAFF';
      const fg = data.textColor || '#29304E';

      const header = args.element.querySelector(
        '.e-popup-header'
      ) as HTMLElement;
      if (header) {
        header.style.background = bg;
        header.style.color = fg;
        // buttons / icons inside header
        const headerEls = header.querySelectorAll('.e-btn, .e-close, .e-icons');
        headerEls.forEach((el: any) => (el.style.color = fg));
      }
      // Conditionally change edit icon to info icon for tasks
      if (
        data.event_reference_type === 'task' ||
        data.event_reference_type === 'ticket'
      ) {
        const editButton = args.element.querySelector('.e-edit') as HTMLElement;
        if (editButton) {
          const iconSpan = editButton.querySelector(
            '.e-btn-icon'
          ) as HTMLElement;
          if (iconSpan) {
            // Clear existing classes and content
            iconSpan.className = 'e-btn-icon';
            iconSpan.innerHTML = '';
            // Add Font Awesome info icon (if available) or use Unicode
            iconSpan.innerHTML = '<i class="fas fa-info-circle"></i>';
            iconSpan.style.color = fg;
            iconSpan.style.fontSize = '14px';

            // Fallback if Font Awesome is not available
            if (!iconSpan.querySelector('i')) {
              iconSpan.innerHTML = 'ℹ️';
              iconSpan.style.fontSize = '16px';
            }
          }

          // Apply color to the button itself
          editButton.style.color = fg;

          // Update button title and aria-label
          editButton.setAttribute('title', 'View Details');
          editButton.setAttribute('aria-label', 'View Details');
        }
      }

      // Optionally tint the content strip (keeps body white)
      const title = args.element.querySelector('.e-subject') as HTMLElement;
      if (title) title.style.color = fg;

      const descriptionElement = args.element.querySelector(
        '.e-description-details'
      ) as HTMLElement;
      if (descriptionElement && data.Description) {
        // Set innerHTML to render HTML tags instead of displaying them as text
        descriptionElement.innerHTML = data.Description;
      }

      // Warehouse deliveries only (opt-in via Input). Surface the
      // delivery status as a coloured pill in the QuickInfo body.
      // The synthetic feed stamps `status: { color, label }`; gated
      // so no other calendar renders it.
      if (
        this.showDeliveryStatusInQuickInfo &&
        this.isDeliveryEvent(data) &&
        data.status?.label
      ) {
        this.injectDeliveryStatusRow(args.element as HTMLElement, data.status);
      }

      // Linked record (Bid/Job/Task/Ticket/PO) — a clickable "{Label} #{number}"
      // line appended after the description, navigating straight to that
      // record's detail page. Matches event-calendar/calendar-list's QuickInfo.
      this.injectReferenceBadge(args.element as HTMLElement, data);
    }
  }

  /** Route + label + icon for each `event_reference_type` we have a detail page for. */
  private static readonly REFERENCE_ROUTES: Record<
    string,
    { path: string; label: string; icon: string }
  > = {
    bid: { path: '/bids/bid', label: 'Bid', icon: 'fa-gavel' },
    job: { path: '/jobs/detail', label: 'Job', icon: 'fa-briefcase' },
    task: { path: '/tasks/task', label: 'Task', icon: 'fa-clipboard-check' },
    ticket: { path: '/tickets/ticket', label: 'Ticket', icon: 'fa-ticket-alt' },
    purchaseorder: { path: '/purchase-order/detail', label: 'Purchase Order', icon: 'fa-file-invoice-dollar' },
  };

  /**
   * Append a plain clickable "{Label} #{number}" line into the QuickInfo
   * popup body, after the description row, when this event is linked to a
   * Bid/Job/Task/Ticket/PO. Idempotent.
   */
  private injectReferenceBadge(popupEl: HTMLElement, data: any): void {
    const content = popupEl?.querySelector('.e-popup-content') as HTMLElement | null;
    if (!content || content.querySelector('.event-reference-link')) {
      return;
    }
    const type = data?.event_reference_type;
    const id = data?.reference_id;
    if (!type || !id) {
      return;
    }
    const route = CalendarComponent.REFERENCE_ROUTES[type];
    if (!route) {
      return;
    }
    const number = data?.reference_number || id;
    // Match the native .e-date-time / .e-description rows exactly (same
    // padding-top, icon size/color/spacing, text size) so this row looks
    // like part of the popup rather than a bolted-on element.
    const link = document.createElement('a');
    link.href = 'javascript:void(0)';
    link.className = 'event-reference-link';
    link.style.cssText =
      'display:flex;align-items:center;padding-top:16px;cursor:pointer;text-decoration:none;';
    link.innerHTML =
      `<span class="e-icons" style="font-size:18px;color:rgba(0,0,0,.54);padding:0 13px 0 0;line-height:1;display:flex;">` +
      `<i class="fas ${route.icon}" style="font-size:16px;line-height:1;"></i></span>` +
      `<span style="font-size:14px;color:rgba(0,0,0,.87);border-bottom:1px solid transparent;">${route.label} #${number}</span>`;
    link.title = `Go to ${route.label} #${number}`;
    link.addEventListener('mouseenter', () => {
      (link.querySelector('span:last-child') as HTMLElement).style.borderBottomColor = 'rgba(0,0,0,.4)';
    });
    link.addEventListener('mouseleave', () => {
      (link.querySelector('span:last-child') as HTMLElement).style.borderBottomColor = 'transparent';
    });
    link.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.router.navigateByUrl(`${route.path}/${id}`);
    });
    content.appendChild(link);
  }

  /**
   * Append a "Status" row (label + coloured pill) into the QuickInfo
   * popup body for a delivery event. Idempotent — guards against a
   * duplicate row if the popup re-renders.
   */
  private injectDeliveryStatusRow(
    popupEl: HTMLElement,
    status: { color?: string; label?: string },
  ): void {
    const content = popupEl.querySelector('.e-popup-content') as HTMLElement | null;
    if (!content || content.querySelector('.delivery-status-row')) {
      return;
    }
    const color = status.color || '#3b82f6';
    const label = status.label || '';
    const row = document.createElement('div');
    row.className = 'delivery-status-row';
    row.style.cssText =
      'display:flex;align-items:center;gap:8px;margin-top:10px;padding-top:10px;border-top:1px solid #eef2f7;';
    row.innerHTML = `
      <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#94a3b8;">Status</span>
      <span style="display:inline-flex;align-items:center;gap:6px;padding:3px 10px;border-radius:999px;font-size:12px;font-weight:600;background:${color}1a;color:${color};">
        <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;"></span>
        ${label}
      </span>`;
    content.appendChild(row);
  }

  private eventDeleteConfirmation(eventData: any): void {
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: {
        title: 'Delete Event',
        message: 'Are you sure you want to remove selected item?',
        description: '',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (eventData?.isAdditionalTimeSlotEvent === true) {
          this.deleteEventSlot(eventData.slotId);
        } else {
          this.deleteEvent(eventData.Id);
        }
      }
    });
  }

  deleteEvent(eventId: number): void {
    this.eventCalendarDataService.deleteEvent(eventId).subscribe({
      next: (response: any) => {
        this.toasterService.showSuccessMessage(response?.message);
        this.refreshEventAppointments(true);
      },
    });
  }

  deleteEventSlot(slotId: number): void {
    this.eventCalendarDataService.deleteEventSlot(slotId).subscribe({
      next: (response: any) => {
        this.toasterService.showSuccessMessage(response?.message);
        this.refreshEventAppointments(true);
      },
    });
  }

  openEventDialog(
    calendarId: any,
    cellData?: any,
    eventData?: any
  ): void {
    if (this.isLocked) {
      return;
    }
    const dialogRef = this.dialog.open(EventDialogComponent, {
      width: '43%',
      height: '87vh',
      data: {
        moduleCalendarId: this.detailCalendarID,
        calendarId: calendarId,
        cellData: cellData,
        editEvent: eventData,
        bidId: this.detailID,
        moduleId: this.moduleID,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshEventAppointments(true);
        this.onEventUpdated.emit(result?.data);

      }
    });
  }

  /**
   * Opens the real Bid edit wizard (the same right-side dialog the Bid
   * detail page's own "Edit" button opens — see DetailBidComponent.
   * onEditBid()), for a bid-linked calendar event's edit icon. Matches
   * the Task/Ticket pattern (openTaskDetail/openTicketDetail) rather
   * than navigating to the Bid's page.
   */
  private openBidEditWizard(bidId: number | string): void {
    this.bidDataService
      .detail(bidId)
      .pipe(first())
      .subscribe({
        next: (res: any) => {
          const model = res?.data;
          if (!model) {
            this.toasterService.showErrorMessage('Unable to load this bid.');
            return;
          }
          this.dialogService.onOpenFromRightDialog(CreateEditBidComponent, {
            isEdit: true,
            model,
          });
          this.dialogService.afterClose()?.subscribe((result: any) => {
            if (result) {
              this.refreshEventAppointments(true);
            }
          });
        },
      });
  }

  openTaskDetail(item: any) {
    this.dialogService.onOpenFromRightDialog(
      ViewTaskDetailComponent,
      {
        id: item?.reference_id,
      },
      1300
    );
    this.dialogService.afterClose()?.subscribe((result: any) => {
      this.refreshEventAppointments(true);
    });
  }

  openTicketDetail(item: any) {
    console.log('item====', item);
    this.dialogService.onOpenFromRightDialog(
      ViewTicketDetailComponent,
      {
        id: item?.reference_id,
      },
      1300
    );
    this.dialogService.afterClose()?.subscribe((result: any) => {
      if (result) {
        this.refreshEventAppointments(true);
      }
    });
  }

  // 1) Add color helpers (same defaults/logic as calendar list)
  private calculateEventColors(calendarCategories: any[]): {
    backgroundColor: string;
    textColor: string;
  } {
    let backgroundColor = '#FBFAFF'; // default bg
    let textColor = '#29304E'; // default readable text

    if (calendarCategories && calendarCategories.length > 0) {
      let mainCategory =
        calendarCategories.find((cat: any) => cat.is_main === 1) ||
        calendarCategories[0];
      if (mainCategory && mainCategory.color) {
        backgroundColor = mainCategory.color;

        // Light background → darken; Dark background → lighten (same as calendar list)
        if (this.isDarkColor(mainCategory.color)) {
          textColor = this.lightenColor(mainCategory.color, 80);
        } else {
          textColor = this.darkenColor(mainCategory.color, 35);
        }
      }
    }
    return { backgroundColor, textColor };
  }

  private hexToRgb(color: string): { r: number; g: number; b: number } | null {
    if (!color) return null;
    let hex = color.replace('#', '').trim();

    // support shorthand #abc
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    if (hex.length !== 6) return null;

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;
    return { r, g, b };
  }

  // YIQ luminance: < 128 considered dark/very dark
  private isDarkColor(color: string, threshold: number = 128): boolean {
    const rgb = this.hexToRgb(color);
    if (!rgb) return false;
    const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return yiq < threshold;
  }

  private lightenColor(color: string, percent: number): string {
    const rgb = this.hexToRgb(color);
    if (!rgb) return '#FFFFFF';
    const p = Math.min(Math.max(percent, 0), 100) / 100;
    const r = Math.round(rgb.r + (255 - rgb.r) * p);
    const g = Math.round(rgb.g + (255 - rgb.g) * p);
    const b = Math.round(rgb.b + (255 - rgb.b) * p);
    const toHex = (v: number) => (v < 16 ? '0' : '') + v.toString(16);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private darkenColor(color: string, percent: number): string {
    const hex = (color || '').replace('#', '');
    if (hex.length !== 6) return '#29304E'; // fallback
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const factor = (100 - percent) / 100;
    const nr = Math.round(r * factor);
    const ng = Math.round(g * factor);
    const nb = Math.round(b * factor);
    const toHex = (n: number) => (n < 16 ? '0' : '') + n.toString(16);
    return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
  }

  /**
   * Get filtered categories excluding main categories (is_main: 1)
   * @param categories - Array of calendar categories
   * @returns Filtered array of non-main categories limited to first 4
   */
  getFilteredCategories(categories: any[]): any[] {
    if (!categories || !Array.isArray(categories)) {
      return [];
    }
    return categories.filter((cat) => cat.is_main === 0).slice(0, 4);
  }

  /**
   * Check if there are any non-main categories to display
   * @param categories - Array of calendar categories
   * @returns boolean indicating if non-main categories exist
   */
  hasNonMainCategories(categories: any[]): boolean {
    if (!categories || !Array.isArray(categories)) {
      return false;
    }
    return categories.filter((cat) => cat.is_main === 0).length > 0;
  }
  initSubscription() {
    this.scrumDataSubscription = this.scrumCommunication
      .getScrumBoardData()
      .subscribe((response) => {
        if (response) {
          this.refreshEventAppointments(true);
        }
      });
  }

  // ============================================================================
  // VIEW MODE & SEARCH FUNCTIONALITY
  // ============================================================================

  /**
   * Set view mode (calendar or list)
   */
  setViewMode(mode: 'calendar' | 'list'): void {
    if (this.viewMode !== mode) {
      this.viewMode = mode;
      // Refresh data when switching views to ensure consistency, especially after search
      // For list view, ensure date range is set before fetching
      if (mode === 'list' && (!this.viewStartDate || !this.viewEndDate)) {
        const now = new Date();
        this.viewStartDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        this.viewEndDate = new Date(now.getFullYear(), now.getMonth() + 6, 0);
      }
      this.refreshEventAppointments(true);
    }
  }

  /**
   * Handle search input changes
   */
  onSearch(): void {
    this.searchInput.next(this.search);
  }

  /**
   * Clear search and refresh
   */
  clearSearch(): void {
    this.search = '';
    this.refreshEventAppointments(true);
  }

  /**
   * Set up the search subscription with debouncing
   */
  private setupSearchSubscription(): void {
    this.searchSubscription = this.searchInput
      .pipe(
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.refreshEventAppointments(true);
      });
  }

  /**
   * Format date for display in list view
   */
  formatDateForDisplay(dateStr: string): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return this.datePipe.transform(date, 'MMM dd, yyyy') || 'N/A';
  }

  /**
   * Format time for display in list view
   */
  formatTimeForDisplay(dateTimeStr: string): string {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return this.datePipe.transform(date, 'h:mm a') || '';
  }

  /**
   * Open edit dialog for event from list view
   */
  editEventFromList(event: any, e: Event): void {
    e.stopPropagation();
    if (!this.isEventEdit()) {
      return;
    }
    // Check if it's a task, ticket, or bid event
    if (event?.event_reference_type === 'task') {
      this.openTaskDetail(event);
      return;
    }
    if (event?.event_reference_type === 'ticket') {
      this.openTicketDetail(event);
      return;
    }
    if (event?.event_reference_type === 'bid' && event?.reference_id) {
      this.openBidEditWizard(event.reference_id);
      return;
    }
    this.openEventDialog(null, null, event);
  }

  /**
   * Delete event from list view
   */
  deleteEventFromList(event: any, e: Event): void {
    e.stopPropagation();
    if (!this.isEventDelete()) {
      return;
    }
    // Transform event data to match the expected format for deletion
    const eventData = {
      Id: event.id,
      isAdditionalTimeSlotEvent: event.isAdditionalTimeSlotEvent || false,
      slotId: event.slotId
    };
    this.eventDeleteConfirmation(eventData);
  }

  /**
   * Track by function for list view ngFor
   */
  trackByEventId(index: number, event: any): any {
    return event?.id || index;
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.leftSidebarMenuToggleSubscription?.unsubscribe();
    this.scrumDataSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
  }

}
